import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { Loader } from "../Loader";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faRotateRight, faSearch, faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { httpClient } from "../HttpClient";
import "../assets/css/IrModels.css";
import "../assets/css/OtaArtifacts.css";

const IR_API = process.env.REACT_APP_IR_MODELS_URL || 'https://dev.mangoe.com/api/device_mgmt_service/admin/ir-json';

function IrModels() {
    const navigate = useNavigate();
    const location = useLocation();

    const [tableData, setTableData] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [brandFilter, setBrandFilter] = useState("");
    const [dropdownModels, setDropdownModels] = useState([]);
    const [highlightedS3Key, setHighlightedS3Key] = useState(location.state?.highlightedS3Key || null);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchText), 400);
        return () => clearTimeout(timer);
    }, [searchText]);

    // Fetch all models once for dropdown options
    useEffect(() => {
        httpClient.get(`${IR_API}/models`, { params: { size: 500 } })
            .then(res => {
                if (res.data?.success) setDropdownModels(res.data.data.content || []);
            })
            .catch(() => {});
    }, []);

    // Fetch paginated table data whenever page/filter/search changes
    useEffect(() => {
        fetchData();
    }, [currentPage, perPage, debouncedSearch, categoryFilter, brandFilter]); // eslint-disable-line

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = { page: currentPage - 1, size: perPage };
            if (debouncedSearch) params.search = debouncedSearch;
            if (categoryFilter) params.category = categoryFilter;
            if (brandFilter) params.brand = brandFilter;
            const res = await httpClient.get(`${IR_API}/models`, { params });
            if (res.data?.success) {
                setTableData(res.data.data.content || []);
                setTotalRows(res.data.data.totalElements || 0);
            }
        } catch {
            toast.error("Failed to load IR models.");
            setTableData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSyncManifest = async () => {
        setSyncing(true);
        try {
            const res = await httpClient.post(`${IR_API}/backfill`);
            if (res.data?.success) {
                toast.success("Manifest synced successfully.");
                fetchData();
            } else {
                toast.error(res.data?.error?.message || "Sync failed.");
            }
        } catch (err) {
            toast.error(err.response?.data?.error?.message || "Sync manifest failed.");
        } finally {
            setSyncing(false);
        }
    };

    // Clear highlight after 3s
    useEffect(() => {
        if (!highlightedS3Key) return;
        const timer = setTimeout(() => setHighlightedS3Key(null), 3000);
        return () => clearTimeout(timer);
    }, [highlightedS3Key]);

    const categories = [...new Set(dropdownModels.map(r => r.category))].sort();
    const brands = [...new Set(
        dropdownModels
            .filter(r => !categoryFilter || r.category === categoryFilter)
            .map(r => r.brand)
    )].sort();

    const handleCategoryChange = (val) => {
        setCategoryFilter(val);
        setBrandFilter("");
        setCurrentPage(1);
    };

    const handleBrandChange = (val) => {
        setBrandFilter(val);
        setCurrentPage(1);
    };

    const handleSearchChange = (val) => {
        setSearchText(val);
        setCurrentPage(1);
    };

    const handleUpdate = (row) => {
        const params = new URLSearchParams({
            category: row.category,
            brand: row.brand,
            modelId: row.modelId,
        });
        navigate(`/ir-models/upload?${params.toString()}`);
    };

    const formatDate = (ts) => {
        if (!ts) return "-";
        return moment.unix(ts).format("YYYY-MM-DD HH:mm");
    };

    const truncateChecksum = (cs) => {
        if (!cs || cs.length <= 12) return cs || "-";
        return `${cs.slice(0, 8)}…`;
    };

    const columns = [
        { name: "Category", selector: row => row.category, sortable: true, width: "120px" },
        { name: "Brand", selector: row => row.brand, sortable: true, width: "135px" },
        { name: "Model ID", selector: row => row.modelId, sortable: true, minWidth: "160px", grow: 1 },
        {
            name: "S3 Key",
            cell: row => <span className="ir-s3-key" title={row.s3Key}>{row.s3Key}</span>,
            minWidth: "250px",
            grow: 2,
        },
        {
            name: "Checksum",
            cell: row => (
                <span className="ir-checksum" title={row.checksum}>{truncateChecksum(row.checksum)}</span>
            ),
            width: "140px",
        },
        { name: "Last Updated", selector: row => formatDate(row.lastUpdated), sortable: true, width: "165px" },
        {
            name: "Actions",
            cell: row => (
                <div className="action-icons">
                    <button
                        onClick={() => handleUpdate(row)}
                        title="Update model JSON"
                        className="btn action-icon"
                    >
                        <FontAwesomeIcon icon={faPen} style={{ color: "#007bff", fontSize: "16px" }} />
                    </button>
                </div>
            ),
            center: true,
            width: "90px",
            ignoreRowClick: true,
            allowOverflow: true,
            style: { position: "sticky", right: 0, backgroundColor: "inherit" },
        },
    ];

    const conditionalRowStyles = [
        {
            when: row => row.s3Key === highlightedS3Key,
            style: { backgroundColor: "rgba(67, 64, 210, 0.1)", transition: "background-color 0.5s ease" },
        },
    ];

    return (
        <div className="custom_tab_wrapper">
            <div className="action_cta">
                <ul className="nav nav-tabs" id="irModelsTab" role="tablist"
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <li className="nav-item" role="presentation">
                        <button className="nav-link active" type="button" style={{ pointerEvents: "none" }}>
                            IR Models: {totalRows}
                        </button>
                    </li>
                </ul>
            </div>

            <div className="table-toolbar custom_table_action">
                <div className="toolbar-left">
                    <div className="ir-search-wrapper">
                        <FontAwesomeIcon icon={faSearch} className="ir-search-icon" />
                        <input
                            type="text"
                            className="form-control ir-search-input"
                            placeholder="Search by brand, model ID or category..."
                            value={searchText}
                            onChange={e => handleSearchChange(e.target.value)}
                        />
                    </div>
                    <select
                        value={categoryFilter}
                        onChange={e => handleCategoryChange(e.target.value)}
                        className="form-select"
                        style={{ width: 150 }}
                    >
                        <option value="">All Categories</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select
                        value={brandFilter}
                        onChange={e => handleBrandChange(e.target.value)}
                        className="form-select"
                        style={{ width: 150 }}
                    >
                        <option value="">All Brands</option>
                        {brands.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                </div>

                <div className="toolbar-right">
                    <button onClick={fetchData} className="btn light_primary_btn" title="Refresh" disabled={loading}>
                        <FontAwesomeIcon icon={faRotateRight} className={loading ? "spin-icon" : ""} />
                    </button>
                    <button
                        onClick={handleSyncManifest}
                        className={`btn primary_btn btn-loader${syncing ? " loading" : ""}`}
                        title="Sync Manifest"
                        disabled={syncing}
                    >
                        Sync Manifest
                    </button>
                </div>
            </div>

            <div className="tab-pane fade show active" role="tabpanel">
                <div className="tab_content_wrapper full_height_tab">
                    <div className="table-responsive h-100vh-150px">
                        {loading ? (
                            <div className="spin-loader"><Loader /></div>
                        ) : (
                            <div className="custom_table customs-table">
                                <DataTable
                                    columns={columns}
                                    data={tableData}
                                    conditionalRowStyles={conditionalRowStyles}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={totalRows}
                                    paginationPerPage={perPage}
                                    paginationRowsPerPageOptions={[10, 25, 50]}
                                    onChangePage={(page) => setCurrentPage(page)}
                                    onChangeRowsPerPage={(newPerPage) => {
                                        setPerPage(newPerPage);
                                        setCurrentPage(1);
                                    }}
                                    fixedHeader
                                    fixedHeaderScrollHeight="430px"
                                    noDataComponent={
                                        <div className="ir-empty-state">
                                            <p>No IR models match the current filters.</p>
                                        </div>
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default IrModels;
